/**
 * Client Information Utility
 * Collects browser, OS, device, and privacy information
 */
import * as UAParser from 'ua-parser-js';
import { detectIncognito } from 'detectincognitojs';

let cached = null;
let inflight = null;

const hasNavigator = () => typeof navigator !== 'undefined' && navigator != null;

function mapDevice(type) {
  switch (type) {
    case 'mobile':
    case 'tablet':
    case 'smarttv':
    case 'wearable':
    case 'console':
    case 'embedded':
      return type;
    default:
      return undefined;
  }
}

const isSecure = () => {
  if (typeof window === 'undefined') return null;
  return window.isSecureContext ?? null;
};

export async function getClientInfo() {
  if (cached) return cached;
  if (inflight) return inflight;

  inflight = (async () => {
    const isBrowser = hasNavigator();
    const raw = isBrowser ? navigator.userAgent : '';

    let browser;
    let os;
    let device;
    let engine;

    if (isBrowser) {
      const uaData = navigator.userAgentData;
      if (uaData?.brands && typeof uaData.mobile === 'boolean') {
        const filtered = uaData.brands.filter((b) => !/^Not/i.test(b.brand));
        const main = filtered[0];
        browser = main ? `${main.brand} ${main.version}` : undefined;
        os = uaData.platform || undefined;
        device = uaData.mobile ? 'mobile' : 'desktop';
      }
    }

    if (!browser || !os || !device) {
      try {
        const parser = new UAParser.UAParser(raw);
        const res = parser.getResult();
        os ||= [res.os?.name, res.os?.version].filter(Boolean).join(' ');
        device ||= mapDevice(res.device?.type) ?? 'desktop';
        engine = [res.engine?.name, res.engine?.version].filter(Boolean).join(' ') || undefined;
      } catch {
        // best-effort
      }
    }

    let incognito = null;
    if (isBrowser) {
      try {
        const res = await detectIncognito();
        incognito = !!res.isPrivate;
      } catch {
        incognito = null;
      }
    }

    const result = {
      browser,
      os,
      device,
      engine,
      raw,
      isIncognito: incognito,
      isSecure: isBrowser ? isSecure() : null,
    };
    cached = result;
    return result;
  })();

  try {
    return await inflight;
  } finally {
    inflight = null;
  }
}
